
import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useEffect, useState, useCallback } from 'react';
import MainFooter from '../components/MainFooter/MainFooter';
import Sidebar from '../components/Sidebar/Sidebar';
import Topbar from '../components/Topbar/Topbar';

const CommonLayout = ({ children }) => {
  // true = sidebar hidden by default, false = sidebar visible
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth < 576);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      // Debounce resize handler for performance
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const width = window.innerWidth;
        setIsMobileView(width < 768);
        setIsSmallMobile(width < 576);
        if (width >= 768) {
          setMobileSidebarOpen(false);
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileSidebarOpen]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileSidebarOpen]);

  const toggleSidebar = useCallback(() => {
    if (isMobileView) {
      setMobileSidebarOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  }, [isMobileView]);

  const closeMobileSidebar = useCallback(() => {
    setMobileSidebarOpen(false);
  }, []);

  return (
    <div className="d-flex flex-column min-vh-100" style={{ width: '100%', overflowX: 'hidden' }}>
      {/* Sidebar Overlay for Mobile */}
      <div
        className={`sidebar-overlay ${isMobileSidebarOpen ? 'show' : ''}`}
        onClick={closeMobileSidebar}
        aria-hidden="true"
      />

      <div className="d-flex flex-grow-1 position-relative">
        {/* Sidebar */}
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          isMobileOpen={isMobileSidebarOpen}
          onClose={closeMobileSidebar}
          isMobileView={isMobileView}
        />

        {/* Main Content */}
        <div
          className="flex-grow-1 d-flex flex-column main-content-wrapper"
          style={{
            marginLeft: !isMobileView && !isSidebarCollapsed ? 'var(--sidebar-width-desktop)' : '0',
            transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: !isMobileView && !isSidebarCollapsed ? 'calc(100% - var(--sidebar-width-desktop))' : '100%',
            maxWidth: !isMobileView && !isSidebarCollapsed ? 'calc(100vw - var(--sidebar-width-desktop))' : '100vw',
          }}
        >
          {/* Topbar */}
          <Topbar
            onToggleSidebar={toggleSidebar}
            isSmallMobile={isSmallMobile}
            isMobileView={isMobileView}
            isSidebarCollapsed={isSidebarCollapsed}
          />

          {/* Main Content */}
          <main
            className="flex-grow-1 p-responsive"
            style={{
              overflowX: 'auto',
              paddingTop: 'var(--topbar-height)',
              maxWidth: '100%',
            }}
          >
            {children}
          </main>
        </div>
      </div>

      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default CommonLayout;
